/**
 * Same payload fields as SignageCreateOrder "Your order is ready" email (view/pay URL + line summary).
 */
import { safeConsole } from "utils/envUtils";
import { getAuthCookie } from "../../utils/authCookies";

/** Matches SalesOrderDetails order totals: sum of `payment_info.amount_paid` and remaining (Overpaid shows +$). */
export function formatOrderReadyPaymentDisplay(order: {
  total_amount?: unknown;
  payment_info?: unknown;
  payment_status?: unknown;
}): { alreadyPaid: string; remainingBalance: string } {
  const orderTotal = Number(order?.total_amount || 0);
  const paidSoFar = (Array.isArray(order?.payment_info) ? order.payment_info : []).reduce(
    (acc: number, p: { amount_paid?: number | string }) => acc + Number(p?.amount_paid || 0),
    0
  );
  const alreadyPaid = `$${paidSoFar.toFixed(2)}`;
  const rawRemaining = orderTotal - paidSoFar;
  const remainingBalance =
    String(order?.payment_status || "") === "Overpaid"
      ? `+$${Math.abs(rawRemaining).toFixed(2)}`
      : `$${rawRemaining.toFixed(2)}`;
  return { alreadyPaid, remainingBalance };
}

/** Line Items card for sendquote emails — Product | Details (Qty + SKU), same card style as Order Summary. */
export function buildOrderReadyLineItemsHtml(items: any[]): string {
  if (!Array.isArray(items) || items.length === 0) {
    return "<p style='color:#6b7280;font-size:14px;margin:0 0 20px 0;'>No items</p>";
  }

  const rows = items
    .map((item) => {
      const name = String(item?.product_name || item?.name || "Product");
      const qty = Number(item?.quantity || 0);
      const sku = String(item?.sku || "").trim() || "—";
      return `
            <tr>
              <td class="email-order-details-field" style="color:#111827;font-weight:500;">${name}</td>
              <td class="email-order-details-value-bold" style="text-align:right;">
                <div>Qty: ${qty}</div>
                <div>SKU: ${sku}</div>
              </td>
            </tr>`;
    })
    .join("");

  return `
        <h3 class="email-heading-h3">Line Items</h3>
        <div class="email-order-details-card">
          <table width="100%" cellpadding="0" cellspacing="0" class="email-order-details-table">
            <tr>
              <td class="email-order-details-field-first">Product</td>
              <td class="email-order-details-value-bold-first" style="text-align:right;">Details</td>
            </tr>
            ${rows}
          </table>
        </div>
  `;
}

/** Single-line address from sales/install/removal address nodes. */
export function formatOrderReadyAddressLine(addrNode: any): string {
  const details =
    addrNode?.address_details && typeof addrNode.address_details === "object"
      ? addrNode.address_details
      : addrNode && typeof addrNode === "object"
        ? addrNode
        : {};
  return [
    details?.addressLine1 || details?.address_line1,
    details?.addressLine2 || details?.address_line2,
    details?.city,
    details?.state || details?.stateCode,
    details?.zip || details?.postal_code,
    details?.country,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .join(", ");
}

/**
 * Order Summary address row — same blue maps link as approve/status emails.
 * Sales: Shipping Address; Installation/Removal: Installation Address.
 */
export function buildOrderReadyAddressFieldRowHtml(
  label: "Shipping Address" | "Installation Address",
  addrNode: any
): string {
  const line = formatOrderReadyAddressLine(addrNode);
  const valueHtml = line
    ? `<a href="https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(line)}" style="color:#2563eb;text-decoration:underline;" target="_blank" rel="noopener noreferrer">${line}</a>`
    : "—";
  return `
            <tr class="email-table-row-even">
              <td class="email-order-details-field">${label}</td>
              <td class="email-order-details-value-link">${valueHtml}</td>
            </tr>`;
}

/** Greeting name for sendquote emails — first + last when available. */
export function buildOrderReadyCustomerDisplayName(order: any): string {
  const fromParts = [
    String(order?.customer_first_name || "").trim(),
    String(order?.customer_last_name || "").trim(),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
  const fullName = String(
    fromParts ||
      order?.user_name ||
      order?.agent_name ||
      order?.team_member_name ||
      ""
  ).trim();
  if (fullName) return fullName;
  const email = String(order?.customer_email || order?.user_email || "").trim();
  if (email.includes("@")) return email.split("@")[0] || "Customer";
  return "Customer";
}

/**
 * Thanks signature company name from GET /marketplace-profiles/created-by/:id
 * (`vendor_name` is the marketplace company/display name).
 */
export async function fetchMarketplaceVendorCompanyName(
  token: string,
  createdById: string
): Promise<string> {
  const api = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const id = String(createdById || "").trim();
  if (!api || !token || !id) return "";
  try {
    const res = await fetch(
      `${api}/marketplace-profiles/created-by/${encodeURIComponent(id)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    if (!res.ok) return "";
    const data = await res.json().catch(() => null);
    const list = Array.isArray(data)
      ? data
      : Array.isArray((data as any)?.data)
        ? (data as any).data
        : [];
    const profile = list[0];
    if (!profile || typeof profile !== "object") return "";
    return String(
      (profile as any).vendor_name ||
        (profile as any).company_name ||
        (profile as any).CompanyName ||
        ""
    ).trim();
  } catch (e) {
    safeConsole.warn("[fetchMarketplaceVendorCompanyName] failed:", e);
    return "";
  }
}

function resolveSendQuoteMarketplaceCreatedById(order: any): string {
  const fromCookie = String(getAuthCookie("userId") || "").trim();
  if (fromCookie) return fromCookie;
  const fromOrder = String(
    order?.signage_id ||
      order?.seller_owner_node_id ||
      (order?.created_by && typeof order.created_by === "object"
        ? order.created_by.id
        : order?.created_by) ||
      ""
  ).trim();
  return fromOrder;
}

export async function buildYourOrderReadyEmailParams(order: any, token: string) {
  const baseUrl = (
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "") ||
    ""
  ).replace(/\/+$/, "");
  const orderIdForPay = String(order?.id || order?._id || "").trim();
  const items = Array.isArray(order?.items) ? order.items : [];
  const firstOrderItem = items[0] || {};
  const orderReadyItemName = String(firstOrderItem?.product_name || "Signage Item");
  const orderReadyQty = String(firstOrderItem?.quantity || 1);
  const orderReadyTotal = `$${Number(order?.total_amount || 0).toFixed(2)}`;
  const customerName = buildOrderReadyCustomerDisplayName(order);

  let projectIdFromQuery = String(order?.project_id || "").trim();
  let accessProjectIdForPay = projectIdFromQuery;
  let accessTokenForPay = "";
  const payUserIdForStripeLinks = String(getAuthCookie("userId") || "").trim();

  if (token && projectIdFromQuery) {
    try {
      const { fetchProjectPublicAccess } = await import("components/proejctsandquoteapicalls");
      const accessRes = await fetchProjectPublicAccess(token, projectIdFromQuery);
      const accessData: any = (accessRes as any)?.data || {};
      const rawAccess: any = accessRes as any;
      accessProjectIdForPay = String(
        accessData.project_id ||
          accessData.projectId ||
          rawAccess?.project_id ||
          projectIdFromQuery ||
          ""
      ).trim();
      accessTokenForPay = String(
        accessData.access_token ||
          accessData.accessToken ||
          rawAccess?.access_token ||
          rawAccess?.accessToken ||
          ""
      ).trim();
      const publicUrlValue = String(
        accessData.public_url || accessData.publicUrl || rawAccess?.public_url || ""
      ).trim();
      if (!accessTokenForPay && publicUrlValue) {
        try {
          const parsed = publicUrlValue.startsWith("http")
            ? new URL(publicUrlValue)
            : new URL(publicUrlValue, window.location.origin);
          accessTokenForPay = String(
            parsed.searchParams.get("access_token") || parsed.searchParams.get("token") || ""
          ).trim();
        } catch {
          /* ignore */
        }
      }
    } catch (e) {
      safeConsole.warn("fetchProjectPublicAccess (order summary email):", e);
    }
  }

  let viewPayUrl = `${baseUrl}/ordersummary?project_id=${encodeURIComponent(accessProjectIdForPay)}&order_id=${encodeURIComponent(orderIdForPay)}&access_token=${encodeURIComponent(accessTokenForPay)}${payUserIdForStripeLinks ? `&supplier_id=${encodeURIComponent(payUserIdForStripeLinks)}` : ""}`;

  try {
    const { createStripePaymentIntent, fetchPaymentGatewaysCreatedByUserId } = await import(
      "components/proejctsandquoteapicalls"
    );
    const payUserId = payUserIdForStripeLinks;
    const orderTotal = Number(order?.total_amount || 0);
    const paidSoFar = (Array.isArray(order?.payment_info) ? order.payment_info : []).reduce(
      (acc: number, p: { amount_paid?: number | string }) => acc + Number(p?.amount_paid || 0),
      0
    );
    const remainingToCharge = Math.max(0, Math.round((orderTotal - paidSoFar) * 100) / 100);
    const amountCents = Math.max(0, Math.round(remainingToCharge * 100));
    let stripeClientSecret = "";
    if (token && payUserId && amountCents > 0) {
      const pi = await createStripePaymentIntent(token, payUserId, {
        amount: amountCents,
        currency: "usd",
      });
      stripeClientSecret = String((pi as any)?.client_secret || (pi as any)?.clientSecret || "").trim();
    }
    let stripePublishableKeyForUrl = "";
    if (token && payUserId) {
      const gateways = await fetchPaymentGatewaysCreatedByUserId(token, payUserId);
      const stripeGw = gateways.find((g: any) => String(g?.gateway || "").toLowerCase() === "stripe");
      stripePublishableKeyForUrl = String(stripeGw?.publishable_key || "").trim();
    }
    const withClientSecret = (u: string) => {
      if (!stripeClientSecret || !u) return u;
      const sep = u.includes("?") ? "&" : "?";
      return `${u}${sep}client_secret=${encodeURIComponent(stripeClientSecret)}`;
    };
    const withPublishableKey = (u: string) => {
      if (!stripePublishableKeyForUrl || !u) return u;
      const sep = u.includes("?") ? "&" : "?";
      return `${u}${sep}publishable_key=${encodeURIComponent(stripePublishableKeyForUrl)}`;
    };
    viewPayUrl = withPublishableKey(withClientSecret(viewPayUrl));
  } catch (e) {
    safeConsole.warn("Stripe helpers (order summary email):", e);
  }

  const { alreadyPaid, remainingBalance } = formatOrderReadyPaymentDisplay(order);
  const marketplaceCompanyName = await fetchMarketplaceVendorCompanyName(
    token,
    resolveSendQuoteMarketplaceCreatedById(order)
  );

  return {
    customerName,
    orderReadyItemName,
    orderReadyQty,
    orderReadyTotal,
    alreadyPaid,
    remainingBalance,
    publicViewPayUrl: viewPayUrl,
    lineItems: buildOrderReadyLineItemsHtml(items),
    addressFieldRow: buildOrderReadyAddressFieldRowHtml(
      "Shipping Address",
      order?.shipping_address || order?.shippingAddress
    ),
    /** Always prefer marketplace vendor_name for Thanks signature on sendquote emails. */
    email_signature_display_name:
      marketplaceCompanyName || String(order?.signage_company_name || "").trim(),
  };
}
